import { findFlowType, visualBuilderFlowType } from './catalog'
import { resolveCuratedType } from './flowTypeCuration'

// A flow's effective flow type id. Inside a Visual Builder integration every flow is the catalog's
// vb flow type, whatever flow type it was saved with (so switching an integration to vb and back
// never loses the original types); elsewhere it is the saved flow type.
const typeIdOf = (flow, integrationType) => visualBuilderFlowType(integrationType)?.id || flow?.flow_type_id || null

const limitOf = flowType => Number.isFinite(flowType?.max_flows) ? flowType.max_flows : null

// Per flow type, how many of an integration's flows use it, against that flow type's license
// `max_flows` (number, or null/absent for unlimited). Layout Builder only warns about going over;
// it never blocks. Ordered by each flow type's first appearance; flows with no type are ignored.
export const effectiveFlowTypeId = typeIdOf

export const flowTypeUsage = (flows, integrationType) => {
  const usage = new Map()
  ;(flows || []).forEach(flow => {
    const flowTypeId = typeIdOf(flow, integrationType)
    if (!flowTypeId) return
    if (!usage.has(flowTypeId)) {
      const flowType = findFlowType(integrationType, flowTypeId)
      usage.set(flowTypeId, {
        flowTypeId,
        name: flowType?.name || flow.flow_type_label || flowTypeId,
        count: 0,
        max: limitOf(flowType)
      })
    }
    usage.get(flowTypeId).count += 1
  })
  return [...usage.values()].map(item => ({ ...item, over: item.max !== null && item.count > item.max }))
}

// One entry per catalog flow type of the integration (in catalog order), including types with no
// flows yet. Flow types a saved flow uses that the catalog no longer has are not listed.
export const usageForCatalogFlowTypes = (flows, integrationType) => {
  const counts = new Map(flowTypeUsage(flows, integrationType).map(item => [item.flowTypeId, item.count]))
  return (integrationType?.flows || []).map(flowType => {
    const count = counts.get(flowType.id) || 0
    const max = limitOf(flowType)
    return { flowTypeId: flowType.id, name: flowType.name, count, max, over: max !== null && count > max }
  })
}

export const anyOverLimit = usage => usage.some(item => item.over)

// uuids of the flows beyond their type's limit: for a type with max M, the first M flows of that
// type (in array order, which is display order) are within the allowance and the rest are over.
export const overLimitFlowUuids = (flows, integrationType) => {
  const seen = new Map()
  const over = new Set()
  ;(flows || []).forEach(flow => {
    const flowTypeId = typeIdOf(flow, integrationType)
    if (!flowTypeId) return
    const position = (seen.get(flowTypeId) || 0) + 1
    seen.set(flowTypeId, position)
    const max = limitOf(findFlowType(integrationType, flowTypeId))
    if (max !== null && position > max) over.add(flow.flow_uuid)
  })
  return over
}

// The limit a picker chip shows: the largest max_flows among the integration's catalog flows that
// roll up into the curated type, or null (show nothing) when there are none or any is unlimited.
const largestLimit = flows => {
  const limits = flows.map(limitOf)
  if (limits.length === 0 || limits.includes(null)) return null
  return Math.max(...limits)
}

export const chipLimit = (integration, curatedName) =>
  largestLimit((integration?.flows || []).filter(flow => resolveCuratedType(flow.id) === curatedName))

// The same rule across all of an integration's catalog flows (used for the Visual Builder card).
export const integrationLimit = integration => largestLimit(integration?.flows || [])
